import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { CopyButton } from '@/components/ui/CopyButton'
import { Dialog, DialogClose, DialogTrigger, FormDialogContent } from '@/components/ui/dialog'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Link } from '@/components/ui/Link'
import { PhoneInput } from '@/components/ui/phoneInput'
import { cn } from '@/lib/utils'
import { Reveal } from '@/routes/Public/marketing/components/Reveal'
import { INCOMPLETE_PHONE_MESSAGE } from '@/utils/phone'
import { Demo, DsSection } from '../primitives'

/** Two fields side by side, collapsing to one column on narrow screens. */
const TWO_UP = 'grid gap-7 sm:grid-cols-2'

/** Beat between consecutive rows on entrance. */
const CASCADE_STEP_MS = 90

/**
 * Staggers a row in on the site's standard entrance. Reveal owns the curve and
 * duration — only the delay varies here, so the cascade stays in step with
 * every other entrance on the site rather than inventing its own timing.
 */
function Row({ cascade, index, children }: { cascade?: boolean; index: number; children: React.ReactNode }) {
  if (!cascade) return <>{children}</>
  return <Reveal delay={index * CASCADE_STEP_MS}>{children}</Reveal>
}

interface CaptiveFormProps {
  /** Prefixes field ids so every demo can render a form on the same page. */
  idPrefix: string
  /** Shows the resolved error state on the fields that can fail validation. */
  invalid?: boolean
  /** Renders on the dark surface — flips token set and the link treatment. */
  onDark?: boolean
  /** Staggers the field rows in, a beat apart, on mount. */
  cascade?: boolean
}

/**
 * Marketing lead capture — the Group Captive form, rebuilt from our own
 * components. The live one on /group-captive is a HubSpot embed inside a
 * cross-origin iframe, so page CSS cannot reach it; this is the Cai-native
 * reference that form's styling should be matched to.
 *
 * Required is the default and goes unmarked; the exception is called out
 * instead, as "(optional)" in the label — the convention the rest of the app
 * already uses. Marking every required field would put a marker on almost all
 * of them and distinguish nothing. `required` on the inputs still carries the
 * semantics for assistive tech and validation.
 */
function CaptiveForm({ idPrefix, invalid, onDark, cascade }: CaptiveFormProps) {
  const [phone, setPhone] = useState(invalid ? '+1415555' : '')

  return (
    <form className="max-w-2xl" onSubmit={(event) => event.preventDefault()}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold tracking-[-0.01em] text-balance">
            See if your fleet qualifies for the Cai Group Captive
          </CardTitle>
        </CardHeader>

        <CardContent>
          <FieldGroup>
            <Row cascade={cascade} index={0}>
              <div className={TWO_UP}>
                <Field>
                  <FieldLabel htmlFor={`${idPrefix}-first`}>First name</FieldLabel>
                  <Input id={`${idPrefix}-first`} autoComplete="given-name" required />
                </Field>
                <Field>
                  <FieldLabel htmlFor={`${idPrefix}-last`}>Last name</FieldLabel>
                  <Input id={`${idPrefix}-last`} autoComplete="family-name" required />
                </Field>
              </div>
            </Row>

            <Row cascade={cascade} index={1}>
              <Field>
                <FieldLabel htmlFor={`${idPrefix}-company`}>Company name</FieldLabel>
                <Input id={`${idPrefix}-company`} autoComplete="organization" required />
              </Field>
            </Row>

            <Row cascade={cascade} index={2}>
              <div className={TWO_UP}>
                <Field data-invalid={invalid || undefined}>
                  <FieldLabel htmlFor={`${idPrefix}-trucks`}>Number of trucks</FieldLabel>
                  <Input
                    id={`${idPrefix}-trucks`}
                    inputMode="numeric"
                    defaultValue={invalid ? 'a few' : undefined}
                    aria-invalid={invalid || undefined}
                    required
                  />
                  {invalid ? <FieldError>Enter a number, like 24.</FieldError> : null}
                </Field>
                <Field data-invalid={invalid || undefined}>
                  <FieldLabel htmlFor={`${idPrefix}-dot`}>DOT number</FieldLabel>
                  <Input
                    id={`${idPrefix}-dot`}
                    inputMode="numeric"
                    defaultValue={invalid ? '00' : undefined}
                    aria-invalid={invalid || undefined}
                    required
                  />
                  {invalid ? <FieldError>Enter your 1–8 digit DOT number.</FieldError> : null}
                </Field>
              </div>
            </Row>

            <Row cascade={cascade} index={3}>
              <div className={TWO_UP}>
                <Field data-invalid={invalid || undefined}>
                  <FieldLabel htmlFor={`${idPrefix}-phone`} optional>
                    Phone number
                  </FieldLabel>
                  <PhoneInput
                    id={`${idPrefix}-phone`}
                    value={phone}
                    onChange={(v) => setPhone(v || '')}
                    error={invalid}
                  />
                  {invalid ? <FieldError>{INCOMPLETE_PHONE_MESSAGE}</FieldError> : null}
                </Field>
                <Field data-invalid={invalid || undefined}>
                  <FieldLabel htmlFor={`${idPrefix}-email`}>Email</FieldLabel>
                  <Input
                    id={`${idPrefix}-email`}
                    type="email"
                    autoComplete="email"
                    defaultValue={invalid ? 'carrie@' : undefined}
                    aria-invalid={invalid || undefined}
                    required
                  />
                  {invalid ? <FieldError>Enter a full email address.</FieldError> : null}
                </Field>
              </div>
            </Row>
          </FieldGroup>
        </CardContent>

        <CardFooter className="flex-col items-stretch gap-5">
          <div className="flex justify-end">
            <Button size="lg" type="submit">
              Submit
            </Button>
          </div>
          {/* Mono fine print, matching the treatment on the live marketing pages. */}
          <p className="text-ink-400 font-mono text-[10px] leading-[1.7]">
            By submitting, you agree Cai may contact you about the Group Captive program. See our{' '}
            <Link
              kind="footer"
              onDark={onDark}
              href="/privacy"
              className="font-mono text-[10px] underline underline-offset-2"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </CardFooter>
      </Card>
    </form>
  )
}

/**
 * The product pattern, as repeated across the app (see EditBusinessDialog):
 * a dialog body — single column, helper text under the fields that need it,
 * and a footer pairing an outline Cancel with the default-variant save.
 * Rendered here as a Card so the shape is visible without a modal.
 */
function ProductForm({ idPrefix }: { idPrefix: string }) {
  return (
    <form className="max-w-md" onSubmit={(event) => event.preventDefault()}>
      <Card>
        <CardHeader>
          <CardTitle>Edit motor carrier</CardTitle>
        </CardHeader>

        <CardContent>
          <ProductFields idPrefix={idPrefix} />
        </CardContent>

        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="outline">
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </CardFooter>
      </Card>
    </form>
  )
}

function ProductFields({ idPrefix }: { idPrefix: string }) {
  return (
    <FieldGroup>
      <Field>
        <FieldLabel htmlFor={`${idPrefix}-name`}>Name</FieldLabel>
        <Input id={`${idPrefix}-name`} defaultValue="Bell Trucking LLC" />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${idPrefix}-dot`}>DOT number</FieldLabel>
        <Input id={`${idPrefix}-dot`} inputMode="numeric" defaultValue="3300318" />
        <FieldDescription>Changing this re-runs the safety lookup.</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor={`${idPrefix}-mc`}>MC number</FieldLabel>
        <Input id={`${idPrefix}-mc`} inputMode="numeric" defaultValue="1088421" />
      </Field>
    </FieldGroup>
  )
}

/** The same form in the real dialog, opened from its button; `saving` shows it mid-save. */
function ProductFormDialog({ idPrefix, saving }: { idPrefix: string; saving?: boolean }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">{saving ? 'Edit motor carrier · saving' : 'Edit motor carrier'}</Button>
      </DialogTrigger>
      <FormDialogContent
        title="Edit motor carrier"
        closeDisabled={saving}
        form={{ onSubmit: (event) => event.preventDefault() }}
        footer={
          <>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={saving}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={saving ? 'Saving…' : undefined}>
              Save
            </Button>
          </>
        }
      >
        <ProductFields idPrefix={idPrefix} />
      </FormDialogContent>
    </Dialog>
  )
}

/**
 * The rows cascade in a beat apart as they come into view. The Card, heading
 * and footer render immediately and only the rows animate, so nothing reflows —
 * a placeholder standing in for the fields would resize the content-width Card
 * as it swapped out. Replay remounts via `key` so the entrance runs again
 * instead of staying settled.
 */
function LoadingCascade() {
  const [run, setRun] = useState(0)

  return (
    <div className="flex flex-col items-start gap-6">
      <CaptiveForm key={run} idPrefix={`cascade-${run}`} cascade />
      <Button variant="outline" onClick={() => setRun((r) => r + 1)}>
        Replay
      </Button>
    </div>
  )
}

/** One reference string plus its copy button, in the page's mono voice. */
function Ref({ value, onDark }: { value: string; onDark?: boolean }) {
  return (
    <div
      className={cn(
        'mt-3 flex items-center gap-1 font-mono text-[10px]',
        onDark ? 'text-ink-300' : 'text-ink-400'
      )}
    >
      <span>{value}</span>
      <CopyButton
        value={value}
        className={cn(
          'hover:text-ink size-4 bg-transparent hover:bg-transparent',
          onDark ? 'text-ink-300' : 'text-ink-400'
        )}
      />
    </div>
  )
}

export function FormSection() {
  return (
    <DsSection id="form" title="Form">
      <Demo label="Marketing lead capture · Card + two-up grid · lg submit">
        <CaptiveForm idPrefix="captive" />
        <Ref value={TWO_UP} />
      </Demo>
      {/* `.dark` flips the tokens the ui components already carry — nothing hand-picked. */}
      <Demo label="Marketing lead capture · on dark" dark>
        <div className="dark">
          <CaptiveForm idPrefix="captive-dark" onDark />
        </div>
        <Ref value='className="dark"' onDark />
      </Demo>
      <Demo label="Product form · dialog body · single column · Cancel + Save">
        <ProductForm idPrefix="product" />
        <Ref value="FieldGroup + FieldDescription" />
      </Demo>
      <Demo
        label="Product form · in its dialog · title scrolls, footer pinned, opens on the first field"
        className="flex flex-wrap items-start gap-8"
      >
        <div>
          <ProductFormDialog idPrefix="product-dialog" />
          <Ref value="<FormDialogContent title footer>" />
        </div>
        <div>
          <ProductFormDialog idPrefix="product-dialog-saving" saving />
          <Ref value="closeDisabled" />
        </div>
      </Demo>
      <Demo label="Loading · rows cascade in as they come into view, 90ms apart">
        <LoadingCascade />
        <Ref value="Reveal delay={index * 90}" />
      </Demo>
      <Demo label="Resolved errors · aria-invalid + FieldError">
        <CaptiveForm idPrefix="captive-invalid" invalid />
        <Ref value="aria-invalid + <FieldError>" />
      </Demo>
    </DsSection>
  )
}
